import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface CalculatorData {
  name: string;
  description: string;
  category: string;
}

interface SEOProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  type?: "website" | "article" | "calculator";
  breadcrumbs?: BreadcrumbItem[];
  faqs?: FAQItem[];
  calculatorData?: CalculatorData;
}

export default function SEO({
  title,
  description,
  canonicalUrl,
  type = "website",
  breadcrumbs,
  faqs,
  calculatorData,
}: SEOProps) {
  const location = useLocation();
  const currentBaseUrl = "https://usmoneyai.com";
  const currentUrl = canonicalUrl || `${currentBaseUrl}${location.pathname}`;

  useEffect(() => {
    // 1. Title Tag
    document.title = title;

    // Helper to edit or create standard tags
    const updateMetaTag = (attributeName: string, attributeValue: string, contentValue: string, isProperty = false) => {
      const selector = isProperty 
        ? `meta[property='${attributeValue}']` 
        : `meta[${attributeName}='${attributeValue}']`;
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        if (isProperty) {
          tag.setAttribute("property", attributeValue);
        } else {
          tag.setAttribute(attributeName, attributeValue);
        }
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", contentValue);
    };

    // 2. Standard Meta Description
    updateMetaTag("name", "description", description);

    // 3. Robots meta parameter for absolute crawling eligibility
    updateMetaTag("name", "robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");

    // 4. Canonical Link Tag in head
    let canonicalTag = document.querySelector("link[rel='canonical']");
    if (!canonicalTag) {
      canonicalTag = document.createElement("link");
      canonicalTag.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.setAttribute("href", currentUrl);

    // 5. Open Graph Meta Tags (for Facebook, LinkedIn, etc.)
    updateMetaTag("", "og:title", title, true);
    updateMetaTag("", "og:description", description, true);
    updateMetaTag("", "og:url", currentUrl, true);
    updateMetaTag("", "og:type", type === "calculator" ? "website" : type, true);
    updateMetaTag("", "og:image", `${currentBaseUrl}/og-image.png`, true);
    updateMetaTag("", "og:site_name", "USMoneyAI", true);

    // 6. Twitter Card Meta Tags
    updateMetaTag("name", "twitter:card", "summary_large_image");
    updateMetaTag("name", "twitter:title", title);
    updateMetaTag("name", "twitter:description", description);
    updateMetaTag("name", "twitter:image", `${currentBaseUrl}/og-image.png`);
    updateMetaTag("name", "twitter:url", currentUrl);

    // 7. Structured Data Core Schema JSON-LD Script Handling
    const injectSchemaScript = (id: string, schemaObj: any) => {
      let scriptTag = document.getElementById(id);
      if (scriptTag) {
        scriptTag.remove();
      }
      scriptTag = document.createElement("script");
      scriptTag.setAttribute("type", "application/ld+json");
      scriptTag.setAttribute("id", id);
      scriptTag.textContent = JSON.stringify(schemaObj);
      document.head.appendChild(scriptTag);
    };

    // A. Organization Schema
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${currentBaseUrl}/#organization`,
      "name": "USMoneyAI",
      "url": `${currentBaseUrl}/`,
      "logo": {
        "@type": "ImageObject",
        "url": `${currentBaseUrl}/favicon.svg`,
        "width": "180",
        "height": "180"
      },
      "description": "Free financial calculators and wealth trackers built with private browser-side calculations.",
      "sameAs": [
        "https://x.com/usmoneyai",
        "https://github.com/usmoneyai"
      ]
    };
    injectSchemaScript("schema-org", organizationSchema);

    // B. Website Schema
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${currentBaseUrl}/#website`,
      "url": `${currentBaseUrl}/`,
      "name": "USMoneyAI",
      "description": "77+ Free calculators for mortgages, savings, retirement, compound and direct debt breakdowns.",
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${currentBaseUrl}/?q={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    };
    injectSchemaScript("schema-website", websiteSchema);

    // C. Breadcrumbs Schema (dynamic if custom items provided, fallback to default for main pages)
    const activeBreadcrumbs = breadcrumbs || [
      { name: "Home", url: `${currentBaseUrl}/` }
    ];
    const breadcrumbListSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": activeBreadcrumbs.map((b, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": b.name,
        "item": b.url.startsWith("http") ? b.url : `${currentBaseUrl}${b.url}`
      }))
    };
    injectSchemaScript("schema-breadcrumbs", breadcrumbListSchema);

    // D. Calculator Schema (SoftwareApplication & FinancialProduct)
    if (type === "calculator" && calculatorData) {
      const calculatorProductSchema = {
        "@context": "https://schema.org",
        "@type": "FinancialProduct",
        "@id": `${currentUrl}/#product`,
        "name": calculatorData.name,
        "description": calculatorData.description,
        "category": calculatorData.category,
        "offers": {
          "@type": "Offer",
          "price": "0.00",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock"
        }
      };

      const calculatorAppSchema = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "@id": `${currentUrl}/#application`,
        "name": calculatorData.name,
        "description": calculatorData.description,
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "All",
        "browserRequirements": "Requires HTML5, CSS3, ES6 Support",
        "offers": {
          "@type": "Offer",
          "price": "0.00",
          "priceCurrency": "USD"
        }
      };

      injectSchemaScript("schema-calculator-product", calculatorProductSchema);
      injectSchemaScript("schema-calculator-app", calculatorAppSchema);
    } else {
      // Remove stale schemas if we aren't displaying a calculator configuration
      document.getElementById("schema-calculator-product")?.remove();
      document.getElementById("schema-calculator-app")?.remove();
    }

    // E. FAQs Schema
    if (faqs && faqs.length > 0) {
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      };
      injectSchemaScript("schema-faq", faqSchema);
    } else {
      document.getElementById("schema-faq")?.remove();
    }

    // Unmount Clean-up
    return () => {
      document.getElementById("schema-org")?.remove();
      document.getElementById("schema-website")?.remove();
      document.getElementById("schema-breadcrumbs")?.remove();
      document.getElementById("schema-calculator-product")?.remove();
      document.getElementById("schema-calculator-app")?.remove();
      document.getElementById("schema-faq")?.remove();
    };
  }, [title, description, currentUrl, type, breadcrumbs, faqs, calculatorData]);

  return null;
}

